//Component is self contained and manages state internally for impressions sub table data

import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Timeline, Row, Col, Tooltip, Avatar, Card, Grid } from 'antd';
import { getMobileOperatingSystem, getReadableDomain, getOutboundLink, isHtml5Creative, getDomainColor, getFaviconUrl, isMeaningfulValue } from './utils';

import {
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  AppleOutlined,
  AndroidOutlined,
  FileImageOutlined,
  Html5Outlined,
  CheckCircleOutlined,
  ExportOutlined,
  CloudOutlined,
} from '@ant-design/icons';

const ellipsisStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const { Item } = Timeline;
const { useBreakpoint } = Grid;

const touchBadgeStyle = {
  display: 'inline-block',
  fontSize: '10px',
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: '4px',
  marginLeft: '8px',
};

const SignupsImpressionsTimeline = ({ impressions, record }) => {
  const [impressionsData, setImpressionsData] = useState([]);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const cellStyle = {
    fontFamily: 'roboto',
    fontSize: '105%',
    fontWeight: 700,
    color: '#636363',
  };
  const sharedStyle = {
    fontFamily: 'roboto',
    fontSize: '105%',
    fontWeight: 700,
    color: '#636363',
  };

  useEffect(() => {
    const sortedImpressions = impressions.sort((a, b) => {
      if (moment(a.IMPRESSION_TIME, moment.DATETIME_LOCAL_MS).isBefore(new Date(b.IMPRESSION_TIME)))
        return -1;
      return 1;
    });

    setImpressionsData(sortedImpressions);
  }, [impressions]);

  const renderSourceAvatar = (domain, inventoryType) => {
    const url = getFaviconUrl(domain, inventoryType);
    if (url) {
      return (
        <Avatar
          shape="square"
          size={isMobile ? 40 : 50}
          icon={<CloudOutlined />}
          src={url}
          style={{ marginRight: isMobile ? 0 : '16px', flexShrink: 0 }}
        />
      );
    }
    if (domain && domain.startsWith('In-App #')) {
      return (
        <Avatar
          shape="square"
          size={isMobile ? 40 : 50}
          style={{
            marginRight: isMobile ? 0 : '16px',
            flexShrink: 0,
            backgroundColor: getDomainColor(domain),
            color: '#fff',
            fontWeight: 600,
          }}
        >
          #
        </Avatar>
      );
    }
    if (domain && domain !== 'Unknown Domain') {
      return (
        <Avatar
          shape="square"
          size={isMobile ? 40 : 50}
          style={{
            marginRight: isMobile ? 0 : '16px',
            flexShrink: 0,
            backgroundColor: getDomainColor(domain),
            color: '#fff',
            fontWeight: 600,
          }}
        >
          {domain.charAt(0).toUpperCase()}
        </Avatar>
      );
    }
    return (
      <Avatar
        shape="square"
        size={isMobile ? 40 : 50}
        icon={<CloudOutlined />}
        style={{ marginRight: isMobile ? 0 : '16px', flexShrink: 0 }}
      />
    );
  };

  const formatAttribution = (method) => {
    if (!method) return '';
    let parsed = method;
    if (typeof method === 'string') {
      try {
        parsed = JSON.parse(method);
      } catch (e) {
        return method;
      }
    }
    const key = Object.keys(parsed)[0];
    if (!key) return '';
    return key
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const makeTimeLine = impressionsData.map((impression, i) => {
    const {
      IMPRESSION_TIME,
      IMPRESSION_USERAGENT,
      IMPRESSION_PAGE_URL,
      IMPRESSION_PUBLISHERNAME,
    } = impression;

    const { impressionDomain, inventoryType } = getReadableDomain(impression, null);

    const APPSITENAME = impressionDomain && impressionDomain !== 'Unknown Domain' ? impressionDomain : '';
    const APPSITENAMETOOLTIP = impressionDomain;
    const APPLICATION_PLATFORM = inventoryType;
    const OS = getMobileOperatingSystem(IMPRESSION_USERAGENT);

    // Timeline is reversed, so array order is opposite of visual order
    const isFirstTouch = i === 0;
    const isLastTouch = i === impressionsData.length - 1;

    const impressionTime = moment(IMPRESSION_TIME, moment.DATETIME_LOCAL_MS).format(
      'MM/DD/YY HH:mm:ss.SSSZ'
    );

    const PUBLISHER = isMeaningfulValue(IMPRESSION_PUBLISHERNAME)
      ? `Published by - ${decodeURI(IMPRESSION_PUBLISHERNAME).replace(/[+_]/g, ' ')}`
      : '';

    let appTypeIcon = () => {
      if (APPLICATION_PLATFORM === 'Application') {
        if (
          OS === 'iPhone' ||
          OS === 'iPad' ||
          OS === 'iPod' ||
          OS === 'Apple Computer'
        ) {
          return <AppleOutlined />;
        }
        if (OS === 'Android') {
          return <AndroidOutlined />;
        }
        return <MobileOutlined />;
      }

      return <DesktopOutlined />;
    };

    let icon = () => {
      if (
        OS === 'Unknown' ||
        OS === 'Chromebook Computer' ||
        OS === 'Windows Computer' ||
        OS === 'Computer'
      ) {
        return <DesktopOutlined />;
      }

      if (OS === 'iPhone' || OS === 'Android' || OS === 'Windows Phone') {
        return <MobileOutlined />;
      }

      if (OS === 'iPad') {
        return <TabletOutlined />;
      }
    };

    const touchBadge = () => {
      if (isFirstTouch) {
        return (
          <span style={{ ...touchBadgeStyle, backgroundColor: '#e6f7ff', color: '#1890ff' }}>
            First Touch
          </span>
        );
      }
      if (isLastTouch) {
        return (
          <span style={{ ...touchBadgeStyle, backgroundColor: '#f6ffed', color: '#52c41a' }}>
            Last Touch
          </span>
        );
      }
      return null;
    };

    if (isMobile) {
      return (
        <Item key={i}>
          <div style={{ padding: '8px 0', width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
                ...sharedStyle,
              }}
            >
              <span>{moment(IMPRESSION_TIME, moment.DATETIME_LOCAL_MS).format('MM/DD/YY HH:mm')}</span>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Impression
                {touchBadge()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <Avatar
                shape="square"
                alt={impression.CREATIVEID}
                size={40}
                icon={isHtml5Creative(IMPRESSION_PAGE_URL) ? <Html5Outlined /> : <FileImageOutlined />}
                src={impression.CREATIVEURL || false}
                style={{
                  backgroundColor:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !impression.CREATIVEURL ? '#e6f7ff' : undefined,
                  color:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !impression.CREATIVEURL ? '#1890ff' : undefined,
                }}
              />
              <div>
                <div style={{ fontWeight: 600, color: '#636363' }}>
                  {icon()} {OS}
                </div>
                <div style={{ fontSize: '90%', color: '#636363' }}>
                  {APPLICATION_PLATFORM}
                  {appTypeIcon()}
                </div>
              </div>
            </div>
            {PUBLISHER && (
              <div style={{ marginBottom: 8, fontSize: '90%', color: '#636363' }}>{PUBLISHER}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {renderSourceAvatar(APPSITENAME, APPLICATION_PLATFORM)}
              <div style={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={APPSITENAMETOOLTIP || ''}>
                  <div style={{ ...ellipsisStyle, fontWeight: 600, color: '#636363' }}>{APPSITENAME}</div>
                </Tooltip>
                {getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS) && (
                  <a
                    href={getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open impression source"
                    title="Open impression source"
                    style={{ fontSize: '90%' }}
                  >
                    <ExportOutlined />
                  </a>
                )}
              </div>
            </div>
          </div>
        </Item>
      );
    }

    return (
      <Item key={i}>
        <Row
          align="middle"
          gutter={[16, 8]}
          style={{
            paddingTop: '5px',
            paddingBottom: '5px',
          }}
        >
          {/* Creative - Avatar */}
          <Col span={3}>
            <div>
              <Avatar
                shape="square"
                alt={impression.CREATIVEID}
                size={50}
                icon={isHtml5Creative(IMPRESSION_PAGE_URL) ? <Html5Outlined /> : <FileImageOutlined />}
                src={impression.CREATIVEURL || false}
                style={{
                  backgroundColor:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !impression.CREATIVEURL ? '#e6f7ff' : undefined,
                  color:
                    isHtml5Creative(IMPRESSION_PAGE_URL) && !impression.CREATIVEURL ? '#1890ff' : undefined,
                }}
              />
            </div>
          </Col>

          {/* Impression Served - Timestamp */}
          <Col span={6}>
            <Tooltip title={impressionTime}>
              <div style={{ ...sharedStyle, ...ellipsisStyle }}>
                {impressionTime}
                {touchBadge()}
              </div>
            </Tooltip>
          </Col>

          {/* Device Type - OS & Icon */}
          <Col span={4}>
            <Tooltip title={OS}>
              <div style={{ ...sharedStyle, ...ellipsisStyle }}>
                {icon()}
                <Tooltip title={IMPRESSION_USERAGENT || ''}>{`  ${OS}`}</Tooltip>
              </div>
            </Tooltip>
          </Col>

          {/* Inventory - App Info */}
          <Col span={3}>
            <Tooltip title={APPLICATION_PLATFORM}>
              <div style={{ ...sharedStyle, ...ellipsisStyle }}>
                {APPLICATION_PLATFORM}
                {appTypeIcon()}
              </div>
            </Tooltip>
          </Col>

          <Col span={3}>
            <Tooltip title={PUBLISHER}>
              <div style={{ ...sharedStyle, ...ellipsisStyle }}>{PUBLISHER}</div>
            </Tooltip>
          </Col>

          {/* Link - App Name and Button */}
          <Col span={5}>
            <div style={{ ...sharedStyle, display: 'flex', flexDirection: 'column' }}>
              <Tooltip title={APPSITENAMETOOLTIP || ''}>
                <div style={{ ...ellipsisStyle }}>{APPSITENAME}</div>
              </Tooltip>
              {getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS) && (
                <a
                  href={getOutboundLink(APPSITENAME, APPLICATION_PLATFORM, OS)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open impression source"
                  title="Open impression source"
                  style={{ fontSize: '90%' }}
                >
                  <ExportOutlined />
                </a>
              )}
            </div>
          </Col>
        </Row>
      </Item>
    );
  });

  return (
    <div style={{ marginRight: isMobile ? 0 : 100 }}>
      <Card
        title="CUSTOMER JOURNEY"
        bodyStyle={{ padding: '0' }}
        headStyle={{
          backgroundColor: '#FAFAFA',
          borderBottom: '1px solid #ddd',
        }}
      >
        {!isMobile && (
          <Row
            align="middle"
            style={{
              paddingTop: '5px',
              paddingBottom: '5px',
              fontWeight: 'bold',
              backgroundColor: '#FAFAFA',
              borderBottom: '1px solid #ddd',
              paddingLeft: '50px',
            }}
            justify="start"
          >
            <Col span={3}>Creative</Col>
            <Col span={6}>Impression Served</Col>
            <Col span={4} style={{ paddingLeft: '10px' }}>
              Device Type
            </Col>
            <Col span={3} style={{ paddingLeft: '20px' }}>
              App Type
            </Col>
            <Col span={3} style={{ paddingLeft: '10px' }}>
              Published By
            </Col>
            <Col span={5} style={{ paddingLeft: '10px' }}>
              Served On
            </Col>
          </Row>
        )}
        <br />
        <Timeline mode={'left'} reverse={true} style={{ padding: '10px', paddingLeft: isMobile ? '20px' : '50px' }}>
          {makeTimeLine}
          <Item dot={<CheckCircleOutlined style={{ fontSize: 16, color: '#52c41a' }} />}>
            <div
              style={{
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                padding: '12px 16px',
                marginLeft: isMobile ? 0 : '50px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />
              <div>
                <div style={{ ...cellStyle, color: '#389e0d' }}>
                  Sign Up Completed — ID: {record.signupId}
                </div>
                <div style={{ fontSize: '90%', color: '#636363' }}>
                  {moment(record.signups_tstamp, moment.DATETIME_LOCAL_MS).format('MM/DD/YY HH:mm')}
                  {record.impressions ? ` • ${record.impressions.length} impressions` : ''}
                  {isMeaningfulValue(record.attributionMethod) ? ` • ${formatAttribution(record.attributionMethod)}` : ''}
                </div>
              </div>
            </div>
          </Item>
        </Timeline>
      </Card>
    </div>
  );
};

export default SignupsImpressionsTimeline;
